/**
 * Error handling utilities for ShippingDocumentsDialog
 */

/**
 * Extracts an error message from an unknown error type
 */
export const extractErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === "object" && err !== null && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }
  return "Unknown error";
};

/**
 * Clears an error state
 */
export const clearError = (setError: (error: string | null) => void) => {
  setError(null);
};

/**
 * Checks if an error is a retryable HTTP error (5xx server errors)
 * These errors are typically transient and may succeed on retry
 */
export const isRetryableError = (error: unknown): boolean => {
  // Check for axios error with status code
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { status?: number } };
    const status = axiosError.response?.status;
    if (status !== undefined) {
      // Retry 5xx errors (server errors) and 429 (rate limit)
      return status >= 500 || status === 429;
    }
  }

  // Check for error with statusCode property
  if (error && typeof error === "object" && "statusCode" in error) {
    const statusCode = (error as { statusCode?: number }).statusCode;
    if (statusCode !== undefined) {
      return statusCode >= 500 || statusCode === 429;
    }
  }

  // Check for network errors (timeout, connection errors)
  if (error instanceof Error) {
    const errorName = error.name;
    const errorMessage = error.message.toLowerCase();
    return (
      errorName === "NetworkError" ||
      errorName === "TimeoutError" ||
      errorMessage.includes("network") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("econnreset") ||
      errorMessage.includes("econnrefused")
    );
  }

  return false;
};

/**
 * Gets the HTTP status code from an error, if available
 */
export const getErrorStatusCode = (error: unknown): number | undefined => {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { status?: number } };
    return axiosError.response?.status;
  }
  if (error && typeof error === "object" && "statusCode" in error) {
    return (error as { statusCode?: number }).statusCode;
  }
  return undefined;
};
